/**
 * RWBR — FILLING A GAP (rulebook p.vi).
 *
 * RULE (p.vi, verbatim):
 *   "When a driver in the TOP or MIDDLE group, or the race LEADER gets
 *    'collected' or has a problem, it will leave an open space on the game
 *    board.  Before the turn ends, you'll need to fill any open spaces.  First,
 *    move all the drivers in the group toward the front, so that any open
 *    space(s) is/are the last space(s) in the group.  Those spaces will be
 *    filled be drivers from the next group.  If the YELLOW flag came out on
 *    either a TROUBLE or PROBLEM result, move the top driver(s) of the next
 *    group into the vacant positions maintaining their order.  If no YELLOW flag
 *    occurred, roll the red die against the 'gas pedal' rating of the top driver
 *    in the next group down.  If you roll equal to or less that driver's 'gas
 *    pedal' rating, he moves into the vacant space.  If not, check the next
 *    driver and roll against HIS 'gas pedal' rating.  Continue doing this until
 *    the open positions in the TOP and MIDDLE groups are filled.  Open positions
 *    don't exist in BOTTOM group since it is a stack."
 *
 * WHAT THIS MODULE IS GIVEN. Not a board with holes in it — a running order that
 * has already closed up (the survivors, in order) plus a count of how many slots
 * each group lost. That is enough to rebuild the board exactly: a group's own
 * survivors are the first `capacity - vacated` cards of that group's slice, and
 * everything after them is the group below waiting its turn. Rebuilding it this
 * way is also what makes the green-flag procedure possible at all — under yellow
 * the closed-up order IS the answer, but under green the car that fills the hole
 * may be the third or the ninth car of the group below, not the first.
 *
 * PURITY. No input is mutated. The returned order is a fresh array of the same
 * `Car` objects, re-stamped with their new positions by `renumber`.
 */

import type { Car } from './types.ts';
import type { Dice } from './dice.ts';
import type { ChipDelta, DiceRecord, Modifier, PositionDelta, RuleEvent } from './ruleEvent.ts';
import { diffOrder, nameOf, renumber } from './ruleEvent.ts';

export const CITE_FILL_GAP =
  'RB/vi — FILLING A GAP ("move all the drivers in the group toward the front … Those spaces will be filled be drivers from the next group.")';

/** Printed capacities. Board A holds the LEADER and six; Board B holds twelve. */
export const GROUP_CAPACITY = { leader: 1, top: 6, middle: 12 } as const;

export type GapGroup = keyof typeof GROUP_CAPACITY;

/** How many slots each board group lost this turn. BOTTOM is a stack and has none. */
export interface Vacancies {
  leader?: number;
  top?: number;
  middle?: number;
}

/**
 * D-EZ-69 — WHAT TOOK A MAN OFF THE BOARD, in the words a person would use.
 *
 * Howard, 20 Aug 2026: *"The Cars off the board notice here isnt really helpful
 * in that regard … Who got knocked out and why?"*
 *
 * ⚠ NO CHART REFERENCES IN HERE. He asked for that specifically, and it is the
 * right call: the citation is already printed under this line and the RACE CAM
 * panel beside it carries the roll. What the sentence owes the reader is the
 * plain fact — a man is gone, this is what happened to him — and repeating
 * "TROUBLE repair chart, YELLOW flag" a third time is not that.
 */
export type LeaveReason =
  | 'wreck'
  | 'problem'
  | 'feud'
  | 'fuel'
  | 'black-flag'
  | 'repair-failed'
  | 'injury'
  | 'lap-down'
  | 'to-the-back';

/** One man whose leaving opened a slot, and where he left from. */
export interface Leaver {
  driverId: string;
  name: string;
  /** The group whose slot he vacated. */
  from: GapGroup;
  /** Where he was running when he left it. */
  position: number;
  why: LeaveReason;
}

export interface FillGapInput {
  /** The survivors in running order, contiguous, index 0 = P1. */
  order: readonly Car[];
  vacated: Vacancies;
  /**
   * p.vi — "If the YELLOW flag came out on either a TROUBLE or PROBLEM result,
   * move the top driver(s) of the next group into the vacant positions."
   */
  yellow: boolean;
  dice: Dice;
  /** Narration: what opened the hole. */
  cause?: string;
  /**
   * Who left, and why. Optional because the rule does not need it — the counts
   * in `vacated` are what fills the slots — and absent it the sentence falls
   * back to the count alone, exactly as it read before.
   */
  leavers?: readonly Leaver[];
}

/** One slot filled, and by whom. */
export interface Promotion {
  driverId: string;
  /** The group the slot belongs to. */
  into: GapGroup;
  /** Where the car sat in the closed-up order before this promotion. */
  fromIndex: number;
  /** Every red die rolled at this slot, in order — the candidates who declined it. */
  redRolls: { driverId: string; red: number; gasPedal: number; promoted: boolean }[];
  /** True when the yellow shortcut promoted him with no roll at all. */
  automatic: boolean;
}

export interface FillGapResult extends RuleEvent {
  eventType: 'fill-gap';
  momentTag: 'gap.none' | 'gap.filled-yellow' | 'gap.filled-green';
  order: Car[];
  promotions: Promotion[];
  /** Chips forfeited by anybody who ended the fill inside the BOTTOM group (p.xv). */
  chipDeltas: ChipDelta[];
  vacated: Required<Vacancies>;
}

/**
 * AMBIGUITY: the rulebook stops the green-flag procedure at "continue doing this
 * until the open positions … are filled" and never says what happens if every
 * driver in the group below rolls outside his gas pedal. The board cannot be
 * left with a hole — the next turn's charts index off fixed slots — so the pass
 * simply continues from the top of the candidate list and keeps rolling. That is
 * the literal reading of "continue … until … filled"; the alternatives (leave the
 * hole, or promote the front car for free) either break the board or hand out a
 * promotion the dice refused. The cap is a runaway guard, not a rule.
 */
const MAX_ROLLS_PER_SLOT = 400;

function count(vacated: Vacancies): Required<Vacancies> {
  const leader = Math.max(0, Math.trunc(vacated.leader ?? 0));
  const top = Math.max(0, Math.trunc(vacated.top ?? 0));
  const middle = Math.max(0, Math.trunc(vacated.middle ?? 0));
  if (leader > GROUP_CAPACITY.leader) throw new RangeError(`the LEADER slot cannot be vacated ${leader} times`);
  if (top > GROUP_CAPACITY.top) throw new RangeError(`the TOP group has ${GROUP_CAPACITY.top} slots, ${top} vacated`);
  if (middle > GROUP_CAPACITY.middle) {
    throw new RangeError(`the MIDDLE group has ${GROUP_CAPACITY.middle} slots, ${middle} vacated`);
  }
  return { leader, top, middle };
}

/** How a man's leaving reads, in a sentence, with no chart in it. */
const WHY: Record<LeaveReason, string> = {
  wreck: 'is out of the race in a wreck',
  problem: 'is out of the race with a problem',
  feud: 'is out of the race in the feud',
  fuel: 'is out of the race, out of fuel',
  'black-flag': 'is out of the race on the black flag',
  'repair-failed': 'is out of the race — the crew could not get him going again',
  injury: 'is out of the race, hurt',
  'lap-down': 'goes a lap down',
  'to-the-back': 'drops to the back of the field',
};

/**
 * ⚠ WHERE HE WAS RUNNING, NOT WHICH GROUP HE WAS IN.
 *
 * The group is already in the second half of the sentence — somebody moves up
 * "into the MIDDLE group" — so naming it twice says the same thing twice and
 * reads like a rulebook. A running position is the more useful fact and the one
 * a spotter would give: "out of the race with a problem from 14th".
 */
function fromWhere(position: number): string {
  return position === 1 ? 'from the lead' : `from ${ordinal(position)}`;
}

function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

function sentenceFor(l: Leaver): string {
  return `${l.name} ${WHY[l.why]} ${fromWhere(l.position)}`;
}

/** "a", "a and b", "a, b and c" — the way somebody would say it out loud. */
function listPeople(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * Fill every open slot on the board, front to back.
 *
 * The LEADER slot is filled from TOP, TOP from MIDDLE, MIDDLE from BOTTOM, and
 * each promotion opens the slot behind it — which is why the order of operations
 * matters and why a single wreck in the TOP group ripples all the way into the
 * stack.
 */
export function fillGaps(input: FillGapInput): FillGapResult {
  const before = [...input.order];
  const vacated = count(input.vacated);
  const dice = input.dice;

  const diceRecords: DiceRecord[] = [];
  const promotions: Promotion[] = [];
  const modifiers: Modifier[] = [
    { name: 'slots vacated', value: vacated.leader + vacated.top + vacated.middle },
    { name: 'flag', value: input.yellow ? 'yellow' : 'green', detail: CITE_FILL_GAP },
  ];

  if (vacated.leader + vacated.top + vacated.middle === 0) {
    return {
      eventType: 'fill-gap',
      momentTag: 'gap.none',
      ruleCitation: CITE_FILL_GAP,
      actorIds: [],
      positionDeltas: [],
      modifiers,
      dice: [],
      description: 'No cards left the board this turn — nothing to fill.',
      order: [...before],
      promotions,
      chipDeltas: [],
      vacated,
    };
  }

  // --- Rebuild the board from the closed-up order ---------------------------
  // The survivors of each group sit at the front of that group's slice; whatever
  // follows them is the group below, which has not been promoted yet.
  const work = [...before];
  const leaderSurvivors = work.splice(0, GROUP_CAPACITY.leader - vacated.leader);
  const topSurvivors = work.splice(0, GROUP_CAPACITY.top - vacated.top);
  const middleSurvivors = work.splice(0, GROUP_CAPACITY.middle - vacated.middle);

  /**
   * The BOTTOM stack, split at the laps-down boundary.
   *
   * READING: a car a lap down is NOT a candidate to fill a hole in the MIDDLE
   * group. Zimmerman's sheet gives the reason for the whole laps-down design in
   * as many words — the split exists "to preserve the game's 'King of the Hill'
   * methodology, in that cars are ranked in order at all times", and he
   * explicitly rejects the alternative in which "a 'laps down' driver might
   * occupy a position in the MIDDLE or even TOP group". Promoting a lapped car
   * into MIDDLE on a gap fill would do precisely that, and would hand a car its
   * lap back for free — a route the printed rules never grant. A hole that only
   * lapped cars are left to fill simply stays open, which is what `fillOne`
   * already does when it runs out of candidates.
   *
   * With the optional rule off, `lapped` is empty and this is the old behaviour
   * exactly.
   */
  const bottom = work.filter((c) => !c.lapsDown); // lead-lap stack, best-placed first
  const lapped = work.filter((c) => c.lapsDown);

  /**
   * One slot. `candidates` is the group below, in running order; the chosen car
   * is spliced out of it and returned.
   *
   * AMBIGUITY: p.vi names "the TOP or MIDDLE group, or the race LEADER" as the
   * places a hole can appear, then describes one procedure for filling holes. The
   * LEADER's "group" is a single space, so "move all the drivers in the group
   * toward the front" is a no-op there and the vacant lead is filled from TOP by
   * the same rule — automatically under yellow, on the red die under green. The
   * alternative (the front TOP car always inherits a vacated lead, even under
   * green) would hand out the race lead with no dice at all, which no other rule
   * in the book does.
   */
  const fillOne = (candidates: Car[], into: GapGroup): Promotion | null => {
    if (candidates.length === 0) return null;

    // p.vi — under yellow the front car(s) of the next group move up, in order.
    if (input.yellow) {
      const car = candidates.shift() as Car;
      return {
        driverId: car.card.driverId,
        into,
        fromIndex: before.findIndex((c) => c.card.driverId === car.card.driverId),
        redRolls: [],
        automatic: true,
      };
    }

    // p.vi — under green, the red die against each candidate's GAS PEDAL in turn.
    const redRolls: Promotion['redRolls'] = [];
    for (let n = 0; n < MAX_ROLLS_PER_SLOT; n++) {
      const i = n % candidates.length;
      const car = candidates[i];
      const red = dice.about([car.card.driverId], () => dice.d6('red'));
      const gasPedal = car.card.gasPedal;
      const promoted = red <= gasPedal;
      redRolls.push({ driverId: car.card.driverId, red, gasPedal, promoted });
      diceRecords.push({
        kind: 'd6',
        dice: [red],
        label: `FILL A GAP — red v ${nameOf(car)}'s gas pedal ${gasPedal}`,
        faces: { red },
        flags: { promoted },
      });
      if (promoted) {
        candidates.splice(i, 1);
        return {
          driverId: car.card.driverId,
          into,
          fromIndex: before.findIndex((c) => c.card.driverId === car.card.driverId),
          redRolls,
          automatic: false,
        };
      }
    }
    throw new Error(
      `FILL A GAP could not fill a ${into.toUpperCase()} slot in ${MAX_ROLLS_PER_SLOT} red-die rolls`,
    );
  };

  for (let i = 0; i < vacated.leader; i++) {
    const p = fillOne(topSurvivors, 'leader');
    if (!p) break;
    leaderSurvivors.push(before.find((c) => c.card.driverId === p.driverId) as Car);
    promotions.push(p);
  }
  // Every card promoted out of TOP (into the lead) has to be replaced too.
  const topSlotsToFill = GROUP_CAPACITY.top - topSurvivors.length;
  for (let i = 0; i < topSlotsToFill; i++) {
    const p = fillOne(middleSurvivors, 'top');
    if (!p) break;
    topSurvivors.push(before.find((c) => c.card.driverId === p.driverId) as Car);
    promotions.push(p);
  }
  const middleSlotsToFill = GROUP_CAPACITY.middle - middleSurvivors.length;
  for (let i = 0; i < middleSlotsToFill; i++) {
    const p = fillOne(bottom, 'middle');
    if (!p) break;
    middleSurvivors.push(before.find((c) => c.card.driverId === p.driverId) as Car);
    promotions.push(p);
  }

  const rebuilt = [...leaderSurvivors, ...topSurvivors, ...middleSurvivors, ...bottom, ...lapped];
  const { order, forfeits } = renumber(rebuilt);
  const positionDeltas: PositionDelta[] = diffOrder(before, order);

  const named = promotions
    .map((p) => {
      const car = before.find((c) => c.card.driverId === p.driverId) as Car;
      const tail = p.automatic
        ? 'moves up under the yellow'
        : `got there on a red ${p.redRolls[p.redRolls.length - 1].red} against ${p.redRolls[p.redRolls.length - 1].gasPedal} gas`;
      return `${nameOf(car)} ${tail} into the ${p.into.toUpperCase()} group`;
    })
    .join('; ');

  const causePhrase = input.cause ? `${input.cause}: ` : '';

  /**
   * ⚠ THE HALF OF THE STORY THIS LINE USED TO LEAVE OUT.
   *
   * "2 open spaces on the boards. Hubie Holder got there on a red 3 …" names
   * only the men who moved UP. The board does not open spaces by itself; a car
   * left, and who it was and what happened to him is the news. Written FIRST,
   * ahead of the promotions, because that is the order it happened in.
   */
  const leaving = (input.leavers ?? []).filter((l) => l.driverId);
  const gone = leaving.length === 0 ? '' : `${listPeople(leaving.map(sentenceFor))}. `;

  return {
    eventType: 'fill-gap',
    momentTag: input.yellow ? 'gap.filled-yellow' : 'gap.filled-green',
    ruleCitation: CITE_FILL_GAP,
    actorIds: promotions.map((p) => p.driverId),
    positionDeltas,
    modifiers,
    dice: diceRecords,
    description:
      `${causePhrase}${gone}` +
      `${promotions.length} open space${promotions.length === 1 ? '' : 's'} on the boards. ` +
      (named || 'Nobody was left to promote.') +
      '.',
    order,
    promotions,
    chipDeltas: forfeits,
    vacated,
  };
}
